/**
 * Authentication API functions
 */

import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { V1Api } from '../generated-types/apis/V1Api'
import { AuthenticationemailVerificationApi } from '../generated-types/apis/AuthenticationemailVerificationApi'
import {
  type APIChangePasswordRequest,
  type APIOrgUserTokenObtainPairRequest,
  type APIRegister,
  type APIRegisterRequest,
  type APITokenRefreshRequest,
  type APIAPIPasswordResetRequestRequest,
  type APIAPIEmailVerificationRequestRequest,
  type APIAPITokenRequestRequest,
  type APIAPIPasswordResetConfirmRequestRequest,
  type APIV1AuthEmailRequestCheckTokenCreate200Response,
} from '../generated-types'
import { Configuration } from '../generated-types/runtime'
import LogTool from '../logger/logTools'

const log = new LogTool({ context: 'AuthAPI', enable: true, logLevel: 'warn' })

export interface LoginCredentials {
  email: string
  password: string
}

export interface SignUpData {
  email: string
  password: string
  password2: string
  firstName?: string
  lastName?: string
  organization: string
}

export interface ResetPasswordData {
  email: string
}

export interface ChangePasswordData {
  oldPassword: string
  newPassword: string
}

export interface TokenResponse {
  access: string
  refresh: string
}

function getApiConfiguration(): { config: Configuration; baseUrl: string } {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }

  const baseUrl = envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl

  return {
    baseUrl,
    config: new Configuration({
      basePath: baseUrl,
      accessToken: () => Cookies.get('access') || '',
    }),
  }
}

const { config: authConfig } = getApiConfiguration()
const v1Api = new V1Api(authConfig)
const emailVerificationApi = new AuthenticationemailVerificationApi(authConfig)

function persistTokens(tokenResponse: TokenResponse): void {
  const accessDecoded = jwtDecode(tokenResponse.access)
  const refreshDecoded = jwtDecode(tokenResponse.refresh)

  Cookies.set('access', tokenResponse.access, {
    expires: new Date((accessDecoded.exp as number) * 1000),
  })
  Cookies.set('refresh', tokenResponse.refresh, {
    expires: new Date((refreshDecoded.exp as number) * 1000),
  })
}

/**
 * Login user and get tokens
 */
export async function loginApi(credentials: LoginCredentials): Promise<TokenResponse> {
  log.info('Logging in user:', credentials.email)

  const requestBody: APIOrgUserTokenObtainPairRequest = {
    email: credentials.email,
    password: credentials.password,
  }

  const apiResponse = await v1Api.v1AuthTokenCreateRaw({
    aPIOrgUserTokenObtainPairRequest: requestBody,
  })

  const data = (await apiResponse.raw.json()) as TokenResponse

  if (!data.access || !data.refresh) {
    throw new Error('Invalid token response')
  }

  persistTokens(data)

  log.info('Login successful')
  return data
}

/**
 * Guest login
 */
export async function guestLoginApi(credentials: LoginCredentials): Promise<TokenResponse> {
  log.info('Guest logging in:', credentials.email)

  // Guest login currently uses the same token endpoint with email/password
  const requestBody: APIOrgUserTokenObtainPairRequest = {
    email: credentials.email,
    password: credentials.password,
  }

  const apiResponse = await v1Api.v1AuthTokenCreateRaw({
    aPIOrgUserTokenObtainPairRequest: requestBody,
  })

  const data = (await apiResponse.raw.json()) as TokenResponse

  if (!data.access || !data.refresh) {
    throw new Error('Invalid token response')
  }

  persistTokens(data)

  log.info('Guest login successful')
  return data
}

/**
 * Sign up new user
 */
export async function signUpApi(signUpData: SignUpData): Promise<APIRegister> {
  log.info('Signing up user:', signUpData.email)

  const payload: APIRegisterRequest = {
    email: signUpData.email,
    password: signUpData.password,
    password2: signUpData.password2,
    firstName: signUpData.firstName,
    lastName: signUpData.lastName,
    organization: signUpData.organization,
  }

  const response = await v1Api.v1AccountsRegisterCreate({
    aPIRegisterRequest: payload,
  })

  log.info('Sign up successful')
  return response
}

/**
 * Request password reset
 */
export async function requestPasswordResetApi(data: ResetPasswordData): Promise<APIV1AuthEmailRequestCheckTokenCreate200Response> {
  log.info('Requesting password reset for:', data.email)

  const requestBody: APIAPIPasswordResetRequestRequest = {
    email: data.email,
  }

  const response = await emailVerificationApi.v1AuthEmailRequestPasswordResetRequestCreate({
    aPIAPIPasswordResetRequestRequest: requestBody,
  })

  log.info('Password reset email sent')
  return response
}

/**
 * Change password (for logged-in user)
 */
export async function changePasswordApi(data: ChangePasswordData): Promise<any> {
  log.info('Changing password')

  const payload: APIChangePasswordRequest = {
    passwordOld: data.oldPassword,
    password: data.newPassword,
    password2: data.newPassword,
  }

  await v1Api.v1AuthChangePasswordUpdate({
    aPIChangePasswordRequest: payload,
  })

  log.info('Password changed successfully')
  return {}
}

/**
 * Verify email with token for email verification (invite/signup)
 */
export async function verifyEmailTokenApi(data: { token: string; password?: string }): Promise<APIV1AuthEmailRequestCheckTokenCreate200Response> {
  log.info('Verifying email token for email verification')

  const requestBody: APIAPIEmailVerificationRequestRequest = {
    token: data.token,
    password: data.password,
  }

  const response = await emailVerificationApi.v1AuthEmailRequestEmailVerificationCreate({
    aPIAPIEmailVerificationRequestRequest: requestBody,
  })

  log.info('Email token verified successfully')
  return response
}

/**
 * Check if password reset token is valid
 */
export async function checkPasswordResetTokenApi(token: string): Promise<APIV1AuthEmailRequestCheckTokenCreate200Response> {
  log.info('Checking password reset token validity')

  const requestBody: APIAPITokenRequestRequest = {
    token: token,
  }

  const response = await emailVerificationApi.v1AuthEmailRequestCheckTokenCreate({
    aPIAPITokenRequestRequest: requestBody,
  })

  log.info('Password reset token is valid')
  return response
}

/**
 * Confirm password reset with token and new password
 */
export async function confirmPasswordResetApi(data: { token: string; password: string }): Promise<APIV1AuthEmailRequestCheckTokenCreate200Response> {
  log.info('Confirming password reset')

  const requestBody: APIAPIPasswordResetConfirmRequestRequest = {
    token: data.token,
    password: data.password,
  }

  const response = await emailVerificationApi.v1AuthEmailRequestPasswordResetConfirmCreate({
    aPIAPIPasswordResetConfirmRequestRequest: requestBody,
  })

  log.info('Password reset confirmed successfully')
  return response
}

/**
 * Refresh access token
 */
export async function refreshAccessTokenApi(): Promise<string> {
  const refreshToken = Cookies.get('refresh')
  
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  log.info('Refreshing access token')

  const requestBody: APITokenRefreshRequest = {
    refresh: refreshToken,
  }

  const response = await v1Api.v1AuthTokenRefreshCreate({
    aPITokenRefreshRequest: requestBody,
  })

  const decoded = jwtDecode(response.access)
  
  Cookies.set('access', response.access, {
    expires: new Date((decoded.exp as number) * 1000),
  })

  log.info('Access token refreshed successfully')
  return response.access
}

/**
 * Logout user (clear tokens)
 */
export function logoutApi(): void {
  log.info('Logging out user')
  
  Cookies.remove('access')
  Cookies.remove('refresh')
  
  log.info('Logout successful')
}
